import type { PropsWithChildren } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import {
    CollapsiblePanel,
    CollapsiblePanelTitle,
} from "app/pages/sales-intelligence/common-components/collapsible-panel";
import type {
    ICollapsiblePanelBaseProps,
    ICollapsiblePanelProvidedProps,
} from "app/pages/sales-intelligence/common-components/collapsible-panel/types";
import type { Translatable } from "app/pages/sales-intelligence/types";

import { FILTERS_PANEL_DEFAULT_WIDTH, FIND_APPS_BUTTON_SIZE } from "../../constants";
import { FilterItemClearButton } from "../FilterItemClearButton/FilterItemClearButton";
import FiltersPanelCollapseBar from "../FiltersPanelCollapseBar/FiltersPanelCollapseBar";
import { StyledFiltersPanelExpandButton } from "../styles";

import {
    StyledFilterCollapsiblePanelHead,
    StyledFiltersPanelContentWrapper,
    StyledFiltersPanelWrapper,
} from "./styles";

interface IFiltersPanelProps extends ICollapsiblePanelBaseProps {
    filtersCount: number;
    expandButtonTooltip?: string;
    clearButtonLabel?: Translatable;
    withBetaTag?: boolean;
    onClear: VoidFunction;
}

export const FiltersPanel = ({
    isExpanded = true,
    width = FILTERS_PANEL_DEFAULT_WIDTH,
    filtersCount,
    expandButtonTooltip = translate("si.lead_gen_filters.button.collapse.tooltip"),
    clearButtonLabel = { key: "common.clear_all" },
    withBetaTag,
    onClear,
    children,
    ...collapsiblePanelProps
}: PropsWithChildren<IFiltersPanelProps>) => {
    const renderCollapsedView = ({ onExpand }: ICollapsiblePanelProvidedProps) => (
        <FiltersPanelCollapseBar
            className="apps-filter-panel-collapsed-bar"
            title={<CollapsiblePanelTitle numberOfFilters={filtersCount} />}
            onExpandClick={onExpand}
        />
    );

    const renderHeaderView = ({ onCollapse }: ICollapsiblePanelProvidedProps) => (
        <StyledFilterCollapsiblePanelHead data-automation="apps-filter-panel.header">
            <CollapsiblePanelTitle
                numberOfFilters={filtersCount}
                withBetaTag={withBetaTag}
            />

            <Flex gap="4px">
                {filtersCount ? (
                    <FilterItemClearButton
                        label={clearButtonLabel}
                        onClick={onClear}
                    />
                ) : null}

                <div className="panelExpandButtonContainer">
                    <StyledFiltersPanelExpandButton
                        iconName="chev-left-n"
                        height={FIND_APPS_BUTTON_SIZE}
                        width={FIND_APPS_BUTTON_SIZE}
                        tooltipText={expandButtonTooltip}
                        onClick={onCollapse}
                    />
                </div>
            </Flex>
        </StyledFilterCollapsiblePanelHead>
    );

    return (
        <CollapsiblePanel
            isExpanded={isExpanded}
            width={width}
            collapsedView={renderCollapsedView}
            headerView={renderHeaderView}
            {...collapsiblePanelProps}
        >
            <StyledFiltersPanelWrapper>
                <StyledFiltersPanelContentWrapper>{children}</StyledFiltersPanelContentWrapper>
            </StyledFiltersPanelWrapper>
        </CollapsiblePanel>
    );
};
